import React, { useState } from 'react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

/**
 * Logo · Bulletproof
 * ---------------------------------------------------------------------------
 * Some browsers on aggressive data-saver / ad-blocker phones (Vivo, MIUI,
 * Realme, Samsung Ultra Data-Saver) block third-party image hosts. When
 * that happens, `<img>` used to fall back to rendering its `alt="logo"`
 * text — which is exactly what showed up on the Vivo V2420 screenshot.
 *
 * This rewrite fixes the issue with 4 layers:
 *   1. Same-origin asset (`/brand-logo.jpg` in `public/`) — no CDN block
 *   2. `<picture>` with WebP + JPG for best-format delivery
 *   3. `onError` swap → inline SVG shield fallback (can never break)
 *   4. `alt=""` — image is decorative, no text ever renders in place
 */

const SIZE_MAP = { xs: 24, sm: 32, md: 44, lg: 64, xl: 96 };

// SVG fallback — a stylised shield with the brand initial. Zero external
// deps, will render instantly if every raster asset in existence is blocked.
const SvgFallback = ({ size, brand }) => (
  <svg
    viewBox="0 0 64 64"
    width={size}
    height={size}
    role="img"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <defs>
      <linearGradient id="ehLogoGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor="#00ff9d" />
        <stop offset="60%" stopColor="#00c67c" />
        <stop offset="100%" stopColor="#008757" />
      </linearGradient>
    </defs>
    <rect width="64" height="64" rx="32" ry="32" fill="#04120a" />
    <path
      d="M32 6 L52 14 V32 C52 46 32 58 32 58 C32 58 12 46 12 32 V14 Z"
      fill="url(#ehLogoGrad)"
      stroke="#00ff9d"
      strokeWidth="1.2"
    />
    <text
      x="32" y="38"
      textAnchor="middle"
      fontFamily="Cinzel, 'Playfair Display', serif"
      fontWeight="900"
      fontSize="22"
      fill="#04120a"
      style={{ letterSpacing: '-0.02em' }}
    >
      {(brand && brand[0] ? brand[0] : 'E').toUpperCase()}
    </text>
  </svg>
);

const Logo = ({ size = 44 }) => {
  const { config } = useSiteConfig();
  const brand = (config.site && config.site.name) || 'ErrorHacker';

  // Prefer the same-origin asset; if the admin has customised the logoUrl
  // (e.g., to a hosted CDN), respect it — but the onError path below still
  // catches broken URLs so we never show alt-text.
  const cfgUrl = config.site && config.site.logoUrl;
  const url = cfgUrl || '/brand-logo.jpg';

  const px = typeof size === 'number' ? size : (SIZE_MAP[size] || 44);
  const [broken, setBroken] = useState(!url);

  return (
    <div
      style={{ width: px, height: px }}
      className="relative rounded-full overflow-hidden shrink-0"
      aria-label={brand}
    >
      {/* Neon halo — always visible even when the image fails */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          boxShadow: '0 0 0 2px var(--eh-green), 0 0 18px rgba(0,255,157,.55), inset 0 0 14px rgba(0,0,0,.7)',
          zIndex: 2,
        }}
      />
      {broken ? (
        <SvgFallback size={px} brand={brand} />
      ) : (
        <picture>
          {/* WebP first for modern browsers (Chrome/Edge/FF/Safari 14+),
              JPG fallback for older Vivo/MIUI browsers. Same origin, no CDN. */}
          {url === '/brand-logo.jpg' && <source srcSet="/brand-logo.webp" type="image/webp" />}
          <img
            src={url}
            alt=""
            width={px}
            height={px}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="w-full h-full object-cover relative"
            style={{ borderRadius: '50%', background: '#04120a' }}
            onError={() => setBroken(true)}
          />
        </picture>
      )}
    </div>
  );
};

export default Logo;
